import { useEffect, useMemo, useState } from "react";
import { AutoComplete, type AutoCompleteCompleteEvent } from "primereact/autocomplete";
import { useAdminEventParticipantGroups } from "@/lib/admin-api/events";

function normalizeGroup(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function ParticipantGroupField(props: {
  id?: string;
  eventId: string;
  value: string | null | undefined;
  onSave: (value: string | null) => void | Promise<void>;
  disabled?: boolean;
}) {
  const groupsQuery = useAdminEventParticipantGroups(props.eventId);
  const savedValue = props.value?.trim() ?? "";
  const [draft, setDraft] = useState(savedValue);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  const allGroups = useMemo(
    () => groupsQuery.data?.groups ?? [],
    [groupsQuery.data?.groups],
  );

  useEffect(() => {
    setDraft(savedValue);
  }, [savedValue]);

  const filterSuggestions = (query: string) => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      // PrimeReact clears its internal searching state only when suggestions
      // changes by reference; reusing allGroups leaves the spinner stuck.
      setSuggestions([...allGroups]);
      return;
    }
    setSuggestions(
      allGroups.filter((group) => group.toLowerCase().includes(normalizedQuery)),
    );
  };

  const commitDraft = () => {
    const next = normalizeGroup(draft);
    const current = normalizeGroup(savedValue);
    if (next === current) return;
    void props.onSave(next);
  };

  const handleComplete = (event: AutoCompleteCompleteEvent) => {
    filterSuggestions(event.query);
  };

  return (
    <AutoComplete
      id={props.id}
      value={draft}
      suggestions={suggestions}
      completeMethod={handleComplete}
      onChange={(event) => {
        setDraft(typeof event.value === "string" ? event.value : "");
      }}
      onSelect={(event) => {
        const selected = typeof event.value === "string" ? event.value : "";
        setDraft(selected);
        const next = normalizeGroup(selected);
        const current = normalizeGroup(savedValue);
        if (next !== current) {
          void props.onSave(next);
        }
      }}
      onBlur={commitDraft}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          commitDraft();
        }
      }}
      onFocus={() => {
        filterSuggestions(draft);
      }}
      forceSelection={false}
      dropdown
      placeholder="Select or enter..."
      className="participant-group-field"
      disabled={props.disabled}
    />
  );
}
